import React from 'react';
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { CanvasAggregationValidatorStatus } from '../services/api';

export type AggregationOverlayLegendStep = {
  aggregationId: string;
  order: number;
  label: string;
  color: string;
  sheetId?: string;
  validatorStatus?: CanvasAggregationValidatorStatus;
  inputs: string[];
  outputs: string[];
  cacheKey?: string;
  lastComputedAt?: string | null;
  lastValidatedAt?: string | null;
  validatorMessage?: string | null;
  fingerprint?: string | null;
};

type AggregationOverlaySummary = {
  total: number;
  validated: number;
  failed: number;
  pending: number;
  missingCache: number;
  lastComputedAt?: string | null;
  lastValidatedAt?: string | null;
};

type AggregationOverlayLegendProps = {
  steps: AggregationOverlayLegendStep[];
  focusedAggregationId: string | null;
  onFocus: (aggregationId: string | null) => void;
  summary?: AggregationOverlaySummary | null;
};

const statusChipColor = (status?: CanvasAggregationValidatorStatus) => {
  switch (status) {
    case 'validated':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const statusChipLabel = (status?: CanvasAggregationValidatorStatus) => {
  switch (status) {
    case 'validated':
      return 'Validated';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      return undefined;
  }
};

const formatTimestamp = (timestamp?: string | null) => {
  if (!timestamp) {
    return null;
  }
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }
  return parsed.toLocaleString();
};

const AggregationOverlayLegend: React.FC<AggregationOverlayLegendProps> = ({
  steps,
  focusedAggregationId,
  onFocus,
  summary,
}) => {
  if (!steps.length) {
    return null;
  }

  return (
    <Paper
      elevation={6}
      sx={{
        p: 2,
        borderRadius: 2,
        maxHeight: 420,
        overflowY: 'auto',
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha('#ffffff', 0.94),
        border: '1px solid rgba(148, 163, 184, 0.3)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Aggregation Overlay
        </Typography>
        <Button
          size="small"
          onClick={() => onFocus(null)}
          disabled={!focusedAggregationId}
          variant="text"
        >
          Show all
        </Button>
      </Stack>

      {summary && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {summary.total} aggregations • {summary.validated} validated • {summary.failed} failed •
            {' '}
            {summary.pending} pending
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cache coverage: {summary.total > 0 ? `${Math.round(((summary.total - summary.missingCache) / summary.total) * 100)}%` : '—'}
          </Typography>
          {(summary.lastComputedAt || summary.lastValidatedAt) && (
            <Typography variant="caption" color="text.secondary">
              {summary.lastComputedAt ? `Last computed ${formatTimestamp(summary.lastComputedAt)}` : 'No computation recorded'}
              {summary.lastValidatedAt ? ` • Last validated ${formatTimestamp(summary.lastValidatedAt)}` : ''}
            </Typography>
          )}
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }}>Steps</Divider>

      <Stack spacing={1.25}>
        {steps.map((step) => {
          const selected = focusedAggregationId === step.aggregationId;
          const stepStatusLabel = statusChipLabel(step.validatorStatus);
          const chipColor = statusChipColor(step.validatorStatus);
          return (
            <Paper
              key={step.aggregationId}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                borderLeft: `4px solid ${step.color}`,
                backgroundColor: selected ? alpha(step.color, 0.12) : 'background.paper',
                boxShadow: selected ? `0 8px 18px ${alpha(step.color, 0.18)}` : 'none',
                transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
              }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: step.color,
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => onFocus(selected ? null : step.aggregationId)}
                  >
                    {selected ? 'Focused' : 'Focus'}
                  </Button>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Aggregation ID: <Typography component="span" variant="caption" fontFamily="monospace">
                    {step.aggregationId}
                  </Typography>
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {step.inputs.length} inputs → {step.outputs.length} outputs
                  {step.sheetId ? ` • Sheet ${step.sheetId}` : ''}
                </Typography>

                {stepStatusLabel && (
                  <Chip
                    size="small"
                    color={chipColor}
                    variant={chipColor === 'default' ? 'outlined' : 'filled'}
                    label={stepStatusLabel}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                )}

                {step.cacheKey && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    Cache {step.cacheKey}
                  </Typography>
                )}

                {(step.lastComputedAt || step.lastValidatedAt) && (
                  <Typography variant="caption" color="text.secondary">
                    {step.lastComputedAt ? `Computed ${formatTimestamp(step.lastComputedAt)}` : 'Never computed'}
                    {step.lastValidatedAt
                      ? ` • Validated ${formatTimestamp(step.lastValidatedAt)}`
                      : ''}
                  </Typography>
                )}

                {step.validatorMessage && (
                  <Typography
                    variant="caption"
                    color={step.validatorStatus === 'failed' ? 'error.main' : 'text.secondary'}
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {step.validatorMessage}
                  </Typography>
                )}

                {step.fingerprint && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    Fingerprint {step.fingerprint}
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default AggregationOverlayLegend;


